import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import Classroom from "../../interfaces/Classroom";
import { dispatch } from "../configureStore";
import Teacher from "./Teacher";
import ReadleeProfile from "../../services/firebase/ReadleeProfile";
import ClassroomAssignmentDetails from "../../interfaces/ClassroomAssignmentDetails";
import Assignment from "../../interfaces/Assignment";

const initialState: Teacher = {
  isLoadingTeacher: true,
  assignments: {},
  sharedAssignments: [],
  starredAssignments: [],
  assignmentClasses: {},
  classrooms: [],
  students: {},
};

const slice = createSlice({
  name: "teacherState",
  initialState,
  reducers: {
    finishLoadingTeacher: state => {
      state.isLoadingTeacher = false;
    },
    setTeacherClassrooms: (state, action: PayloadAction<Array<Classroom>>) => {
      state.classrooms = action.payload;
    },
    addAssignment: (state, action: PayloadAction<Assignment>) => {
      const assignmentId = action.payload.id;
      // If assignment does NOT already exist on state, set all info
      const localAssignment = state.assignments[assignmentId];
      if (!localAssignment) {
        state.assignments = {
          // All other assignments
          ...state.assignments,
          // Assignment being added
          [assignmentId]: {
            id: assignmentId,
            assignmentDetails: action.payload,
            totalWords: 0,
          },
        };
        // If assignment does exist on state, only update assignment details
      } else {
        state.assignments = {
          // All other assignments
          ...state.assignments,
          // Assignment being updated
          [assignmentId]: {
            // All other properties on assignment
            ...localAssignment,
            // Just update assignment details
            assignmentDetails: action.payload,
          },
        };
      }
    },
    addTotalWordsToAssignment: (state, action: PayloadAction<{ assignmentId: string; totalWords: number }>) => {
      const localAssignment = state.assignments[action.payload.assignmentId];
      if (localAssignment) {
        state.assignments = {
          // All other assignments
          ...state.assignments,
          // Assignment being updated
          [action.payload.assignmentId]: {
            // All other properties on assignment
            ...localAssignment,
            // Just update assignment details
            totalWords: action.payload.totalWords,
          },
        };
      }
    },
    removeAssignment: (state, action: PayloadAction<string>) => {
      delete state.assignments[action.payload];
    },
    removeAllAssignments: state => {
      state.assignments = {};
    },
    setSharedAssignments: (state, action: PayloadAction<Array<string>>) => {
      state.sharedAssignments = action.payload;
    },
    setStarredAssignments: (state, action: PayloadAction<Array<string>>) => {
      state.starredAssignments = action.payload;
    },
    addAssignmentClass: (state, action: PayloadAction<ClassroomAssignmentDetails>) => {
      const assignmentId = action.payload.id;
      state.assignmentClasses = {
        // All other assignments
        ...state.assignmentClasses,
        // Assignment being updated
        [assignmentId]: {
          // All other classes on assignment
          ...state.assignmentClasses[assignmentId],
          // Class being added
          [action.payload.classData.id]: action.payload,
        },
      };
    },
    removeClassFromAssignmentClasses: (
      state,
      action: PayloadAction<{ classId: string; classAssignments: Array<string> | undefined }>,
    ) => {
      // For each assignment
      for (const assignment in state.assignmentClasses) {
        // If the assignment has the classid in classes
        if (state.assignmentClasses[assignment]?.hasOwnProperty(action.payload.classId)) {
          // If the list of class assignments is undefined or the assignment is NOT on that list
          if (
            action.payload.classAssignments === undefined ||
            action.payload.classAssignments.includes(assignment) === false
          ) {
            // Delete the class from the assignment
            delete state.assignmentClasses[assignment]?.[action.payload.classId];
          }
        }
      }
    },
    removeAllAssignmentClasses: state => {
      state.assignmentClasses = {};
    },
    addStudentToTeacher: (state, action: PayloadAction<ReadleeProfile>) => {
      state.students = { ...state.students, [action.payload?.uid || ""]: action.payload };
    },
    removeStudentFromTeacher: (state, action: PayloadAction<string>) => {
      delete state.students[action.payload];
    },
  },
});

export const finishLoadingTeacher = (): void => {
  dispatch(slice.actions.finishLoadingTeacher());
};

export const addAssignment = (assignmentDetails: Assignment) => {
  dispatch(slice.actions.addAssignment(assignmentDetails));
};

export const addTotalWordsToAssignment = (assignmentId: string, totalWords: number) => {
  dispatch(slice.actions.addTotalWordsToAssignment({ assignmentId, totalWords }));
};

export const removeAssignment = (assignmentId: string) => {
  dispatch(slice.actions.removeAssignment(assignmentId));
};

export const removeAllAssignments = () => {
  dispatch(slice.actions.removeAllAssignments());
};

export const setSharedAssignments = (sharedAssignments: Array<string>) => {
  dispatch(slice.actions.setSharedAssignments(sharedAssignments));
};

export const setStarredAssignments = (starredAssignments: Array<string>) => {
  dispatch(slice.actions.setStarredAssignments(starredAssignments));
};

export const addAssignmentClass = (classroomAssignmentDetails: ClassroomAssignmentDetails) => {
  dispatch(slice.actions.addAssignmentClass(classroomAssignmentDetails));
};

export const removeClassFromAssignmentClasses = (classId: string, classAssignments: Array<string> | undefined) => {
  dispatch(slice.actions.removeClassFromAssignmentClasses({ classId, classAssignments }));
};

export const removeAllAssignmentClasses = () => {
  dispatch(slice.actions.removeAllAssignmentClasses());
};

export const setTeacherClassrooms = (classrooms: Array<Classroom>) => {
  dispatch(slice.actions.setTeacherClassrooms(classrooms));
};

export const addStudentToTeacher = (student: ReadleeProfile) => {
  dispatch(slice.actions.addStudentToTeacher(student));
};

export const removeStudentFromTeacher = (studentId: string) => {
  dispatch(slice.actions.removeStudentFromTeacher(studentId));
};

export default slice.reducer;
